import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteConfirmDialog } from '@components/DeleteConfirmDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOffOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReplayIcon from '@mui/icons-material/Replay';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { ClaimV2Item } from '../../../store';
import {
  useClaimAttention,
  useClaimSubmission,
  useClaimSubmit,
  useClaimX12Download,
  useLazyGetStediPdfQuery,
  useResubmitClaim,
} from '../../../store';
import { CorrectionPreparationDialog } from '../../CorrectionPreparation/CorrectionPreparationDialog';
import { canDispositionClaim, isActivelyNeedingAttention } from '../shared';

import { ResubmitErrorDialog } from './ResubmitErrorDialog';

interface ClaimRowActionsProps {
  claim: ClaimV2Item;
}

const SUBMITTABLE_PHASES = ['ready'];
const CORRECTION_PHASES = ['review'];
const DELETABLE_PHASES = ['drafts', 'ready'];
// PDF available for submitted+ claims
const PDF_PHASES = ['inFlight', 'paid', 'review'];

/** Extract a human-readable error message from an RTK Query error */
function extractErrorPayload(error: unknown): { message?: string; error_code?: string } {
  if (!error || typeof error !== 'object') return {};
  // RTK Query FetchBaseQueryError has a `data` field with the API response body
  const data = (error as Record<string, unknown>).data;
  if (data && typeof data === 'object') {
    return data;
  }
  return {};
}

export const ClaimRowActions: React.FC<ClaimRowActionsProps> = ({ claim }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const open = Boolean(anchorEl);

  // Resubmit error dialog state
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [resubmitError, setResubmitError] = useState<{
    error: { message?: string; error_code?: string };
    type: '7' | '8';
  } | null>(null);

  const { submitClaim: submitToOffice, isSubmitting } = useClaimSubmit();
  const { deleteClaim } = useClaimSubmission();
  const { downloadClaim: downloadClaimX12, isDownloading: isDownloadingX12 } = useClaimX12Download();
  const [getStediPdf, { isFetching: isDownloadingPdf }] = useLazyGetStediPdfQuery();
  const { resubmitClaim, isResubmitting } = useResubmitClaim();
  const { dismiss: dismissAttention, remindTomorrow, isUpdating: isUpdatingAttention } = useClaimAttention();

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleEditOrView = useCallback(() => {
    handleClose();
    navigate(`/jotbill/claim/${claim.claim_id}`);
  }, [navigate, claim.claim_id, handleClose]);

  const handleSubmit = useCallback(async () => {
    handleClose();
    try {
      await submitToOffice(claim.claim_id);
      enqueueSnackbar('Claim submitted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to submit claim', { variant: 'error' });
    }
  }, [claim.claim_id, submitToOffice, enqueueSnackbar, handleClose]);

  const handleDownloadPdf = useCallback(async () => {
    handleClose();
    try {
      const result = await getStediPdf({ claimId: claim.claim_id }).unwrap();
      if (result?.pdf_data) {
        const byteCharacters = atob(result.pdf_data);
        const byteNumbers = new Array(byteCharacters.length) as number[];
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch {
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
  }, [claim.claim_id, getStediPdf, enqueueSnackbar, handleClose]);

  const handleDownloadX12 = useCallback(async () => {
    handleClose();
    try {
      await downloadClaimX12(claim.claim_id);
    } catch {
      enqueueSnackbar('Failed to download X12', { variant: 'error' });
    }
  }, [claim.claim_id, downloadClaimX12, enqueueSnackbar, handleClose]);

  const handleDelete = useCallback(async () => {
    setDeleteOpen(false);
    try {
      await deleteClaim(claim.claim_id);
      enqueueSnackbar('Claim deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to delete claim', { variant: 'error' });
    }
  }, [claim.claim_id, deleteClaim, enqueueSnackbar]);

  const handleDismissAttention = useCallback(async () => {
    handleClose();
    const result = await dismissAttention(claim.claim_id);
    if (result.success) {
      billing.claimAttentionDismissed(claim.claim_id, {
        source: 'claims_list',
        attention_lane: claim.era_triage?.attention_lane ?? undefined,
        signals: claim.era_triage?.signals,
      });
      // No success snackbar — the row's disposition chip + dim treatment is the feedback.
    } else {
      billing.claimAttentionFailed(claim.claim_id, {
        action: 'dismiss',
        source: 'claims_list',
        error_code: extractErrorPayload(result.error).error_code,
      });
      enqueueSnackbar('Failed to dismiss', { variant: 'error' });
    }
  }, [claim.claim_id, claim.era_triage, dismissAttention, enqueueSnackbar, handleClose, billing]);

  const handleRemindTomorrow = useCallback(async () => {
    handleClose();
    const result = await remindTomorrow(claim.claim_id);
    if (result.success) {
      billing.claimAttentionSnoozed(claim.claim_id, {
        source: 'claims_list',
        attention_lane: claim.era_triage?.attention_lane ?? undefined,
        signals: claim.era_triage?.signals,
      });
      // No success snackbar — the row's "Snoozed until X" chip is the feedback.
    } else {
      billing.claimAttentionFailed(claim.claim_id, {
        action: 'snooze',
        source: 'claims_list',
        error_code: extractErrorPayload(result.error).error_code,
      });
      enqueueSnackbar('Failed to snooze', { variant: 'error' });
    }
  }, [claim.claim_id, claim.era_triage, remindTomorrow, enqueueSnackbar, handleClose, billing]);

  const handleResubmit = useCallback(async (code: '7' | '8') => {
    handleClose();
    if (code === '7') { setCorrectionOpen(true); return; }
    const result = await resubmitClaim(claim.claim_id, code);
    if (result.success && result.data) {
      const label = 'Void';
      enqueueSnackbar(`${label} claim created`, { variant: 'success' });
      navigate(`/jotbill/claim/${result.data.id}`);
    } else {
      // Show actionable error dialog instead of generic snackbar
      setResubmitError({
        error: extractErrorPayload(result.error),
        type: code,
      });
    }
  }, [claim.claim_id, resubmitClaim, navigate, enqueueSnackbar, handleClose]);

  const canSubmit = SUBMITTABLE_PHASES.includes(claim.phase) && (claim.error_count ?? 0) === 0;
  const canDelete = DELETABLE_PHASES.includes(claim.phase);
  const canCorrect = CORRECTION_PHASES.includes(claim.phase);
  const canDownloadPdf = PDF_PHASES.includes(claim.phase);
  const needsAttention = isActivelyNeedingAttention(claim);
  const canDisposition = canDispositionClaim(claim);
  const isLoading = isSubmitting || isDownloadingX12 || isDownloadingPdf || isUpdatingAttention;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{ p: 0.5 }}
        aria-label="Claim actions"
      >
        {isLoading ? <CircularProgress size={16} /> : <MoreVertIcon sx={{ fontSize: 18 }} />}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: { sx: { minWidth: 180, mt: 0.5 } },
        }}
      >
        <MenuItem onClick={handleEditOrView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
            View Claim
          </ListItemText>
        </MenuItem>

        {canSubmit && (
          <MenuItem onClick={handleSubmit} disabled={isSubmitting}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Submit Claim
            </ListItemText>
          </MenuItem>
        )}

        <Divider />

        {canDownloadPdf && (
          <MenuItem onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
            <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Download PDF
            </ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleDownloadX12} disabled={isDownloadingX12}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
            Download X12
          </ListItemText>
        </MenuItem>

        {canDisposition && [
          <Divider key="div-attn" />,
          <MenuItem key="remind-tomorrow" onClick={handleRemindTomorrow} disabled={isUpdatingAttention}>
            <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Remind me tomorrow
            </ListItemText>
          </MenuItem>,
          <MenuItem key="dismiss-attn" onClick={handleDismissAttention} disabled={isUpdatingAttention}>
            <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Dismiss
            </ListItemText>
          </MenuItem>,
        ]}

        {canCorrect && [
          <Divider key="div-corr" />,
          <MenuItem key="correction" onClick={() => handleResubmit('7')} disabled={isResubmitting}>
            <ListItemIcon><ReplayIcon fontSize="small" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Create Correction
            </ListItemText>
          </MenuItem>,
          <MenuItem key="void" onClick={() => handleResubmit('8')} disabled={isResubmitting}>
            <ListItemIcon><ReplayIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Void Claim
            </ListItemText>
          </MenuItem>,
        ]}

        {canDelete && [
          <Divider key="div-del" />,
          <MenuItem key="delete" onClick={() => { handleClose(); setDeleteOpen(true); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem', color: 'error.main' } } }}>
              Delete Claim
            </ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        type="claim"
      />

      {correctionOpen && claim.claim_id && <CorrectionPreparationDialog
        claimId={claim.claim_id}
        onClose={() => setCorrectionOpen(false)}
        onOpened={id => navigate(`/jotbill/claim/${id}`)}
      />}
      <ResubmitErrorDialog
        open={resubmitError !== null}
        onClose={() => setResubmitError(null)}
        error={resubmitError?.error ?? null}
        encounterId={claim.encounter_id}
        claimId={claim.claim_id}
        resubmitType={resubmitError?.type ?? '7'}
      />
    </>
  );
};
